import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Building2, UserCheck, CalendarDays, GraduationCap, CheckCircle2,
  ArrowRight, ArrowLeft, Loader2, Check, Eye, EyeOff, Plus, Trash2,
  SkipForward, Rocket
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Stepper metadata ────────────────────────────────────────────────────────
const STEPS = [
  { id: 1, title: 'Platform Info',   icon: Building2,      desc: 'Name, tagline & timezone' },
  { id: 2, title: 'First Teacher',   icon: UserCheck,      desc: 'Create a teacher account' },
  { id: 3, title: 'Class Schedule',  icon: CalendarDays,   desc: 'Set available time slots' },
  { id: 4, title: 'First Student',   icon: GraduationCap,  desc: 'Add a student account' },
  { id: 5, title: 'Review',          icon: CheckCircle2,   desc: 'Confirm & launch' },
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_LABELS = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };

const TIMEZONES = [
  { value: 'America/Sao_Paulo',  label: 'São Paulo (UTC-3)' },
  { value: 'America/Recife',     label: 'Recife (UTC-3)' },
  { value: 'America/New_York',   label: 'New York (UTC-5)' },
  { value: 'America/Chicago',    label: 'Chicago (UTC-6)' },
  { value: 'America/Los_Angeles',label: 'Los Angeles (UTC-8)' },
  { value: 'Europe/London',      label: 'London (UTC+0)' },
  { value: 'Europe/Lisbon',      label: 'Lisbon (UTC+0)' },
  { value: 'Europe/Paris',       label: 'Paris (UTC+1)' },
  { value: 'Europe/Madrid',      label: 'Madrid (UTC+1)' },
  { value: 'Asia/Tokyo',         label: 'Tokyo (UTC+9)' },
  { value: 'Australia/Sydney',   label: 'Sydney (UTC+10)' },
];

// ── Shared input style ───────────────────────────────────────────────────────
const inputCls = 'w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] focus:ring-2 focus:ring-[#1A2E6E]/10 outline-none transition-all';

// ── Step 1: Platform Info ────────────────────────────────────────────────────
const Step1 = ({ data, onChange }) => (
  <div className="space-y-5">
    <div>
      <label className="block text-sm font-medium text-[#1A2E6E] mb-1.5">
        Platform / School Name <span className="text-[#D72B2B]">*</span>
      </label>
      <input
        type="text"
        value={data.site_name || ''}
        onChange={e => onChange({ ...data, site_name: e.target.value })}
        placeholder="e.g. Frankly Talking English"
        className={inputCls}
        maxLength={80}
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-[#1A2E6E] mb-1.5">Tagline</label>
      <input
        type="text"
        value={data.site_tagline || ''}
        onChange={e => onChange({ ...data, site_tagline: e.target.value })}
        placeholder="e.g. Speak English with Confidence"
        className={inputCls}
        maxLength={120}
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-[#1A2E6E] mb-1.5">
        Contact Email <span className="text-[#D72B2B]">*</span>
      </label>
      <input
        type="email"
        value={data.contact_email || ''}
        onChange={e => onChange({ ...data, contact_email: e.target.value })}
        placeholder="hello@yourschool.com"
        className={inputCls}
      />
    </div>
    <div>
      <label className="block text-sm font-medium text-[#1A2E6E] mb-1.5">Timezone</label>
      <select
        value={data.timezone || 'America/Sao_Paulo'}
        onChange={e => onChange({ ...data, timezone: e.target.value })}
        className={inputCls + ' bg-white'}
      >
        {TIMEZONES.map(tz => (
          <option key={tz.value} value={tz.value}>{tz.label}</option>
        ))}
      </select>
    </div>
  </div>
);

// ── Step 2: Create Teacher ────────────────────────────────────────────────────
const Step2 = ({ data, onChange, onCreated }) => {
  const [showPass, setShowPass] = useState(false);
  const [creating, setCreating] = useState(false);

  const create = async () => {
    if (!data.name?.trim() || !data.email?.trim() || !data.password?.trim()) {
      toast.error('Name, email and password are required');
      return;
    }
    if (data.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/users`, {
        name: data.name.trim(),
        email: data.email.trim(),
        password: data.password,
        phone: data.phone?.trim() || null,
        role: 'teacher',
        status: 'approved',
      });
      onCreated({ ...data, created: true, teacher_id: res.data.id });
      toast.success(`Teacher account created for ${data.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create teacher');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-5">
      {data.created && (
        <div className="flex items-center gap-3 p-4 bg-[#4CAF50]/10 border border-[#4CAF50]/30 rounded-xl">
          <Check className="w-5 h-5 text-[#4CAF50] shrink-0" />
          <p className="text-sm font-medium text-[#4CAF50]">
            Teacher account created for <strong>{data.name}</strong>
          </p>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-[#1A2E6E] mb-1.5">Full Name <span className="text-[#D72B2B]">*</span></label>
        <input type="text" value={data.name || ''} onChange={e => onChange({ ...data, name: e.target.value })}
          placeholder="e.g. Sarah Johnson" className={inputCls} disabled={data.created} />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#1A2E6E] mb-1.5">Email <span className="text-[#D72B2B]">*</span></label>
        <input type="email" value={data.email || ''} onChange={e => onChange({ ...data, email: e.target.value })}
          placeholder="teacher@yourschool.com" className={inputCls} disabled={data.created} />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#1A2E6E] mb-1.5">Password <span className="text-[#D72B2B]">*</span></label>
        <div className="relative">
          <input type={showPass ? 'text' : 'password'} value={data.password || ''}
            onChange={e => onChange({ ...data, password: e.target.value })}
            placeholder="At least 8 characters" className={inputCls + ' pr-12'} disabled={data.created} />
          <button type="button" onClick={() => setShowPass(v => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1A2E6E]/40 hover:text-[#1A2E6E]">
            {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-[#1A2E6E] mb-1.5">Phone <span className="text-[#1A2E6E]/40">(optional)</span></label>
        <input type="tel" value={data.phone || ''} onChange={e => onChange({ ...data, phone: e.target.value })}
          placeholder="+55 11 99999-9999" className={inputCls} disabled={data.created} />
      </div>
      {!data.created && (
        <button onClick={create} disabled={creating}
          className="w-full flex items-center justify-center gap-2 py-3 bg-[#1A2E6E] text-white rounded-xl hover:bg-[#16275C] transition-colors font-medium disabled:opacity-50">
          {creating ? <Loader2 size={18} className="animate-spin" /> : <UserCheck size={18} />}
          Create Teacher Account
        </button>
      )}
    </div>
  );
};

// ── Step 3: Class Schedule ────────────────────────────────────────────────────
const Step3 = ({ data, onChange }) => {
  const schedule = data.schedule || {};
  const duration = data.session_duration || 60;

  const toggleDay = (day) => {
    const current = schedule[day] || [];
    if (current.length > 0) {
      onChange({ ...data, schedule: { ...schedule, [day]: [] } });
    } else {
      onChange({ ...data, schedule: { ...schedule, [day]: ['09:00'] } });
    }
  };

  const addSlot = (day) => {
    const current = schedule[day] || [];
    onChange({ ...data, schedule: { ...schedule, [day]: [...current, '10:00'] } });
  };

  const updateSlot = (day, idx, val) => {
    const updated = [...(schedule[day] || [])];
    updated[idx] = val;
    onChange({ ...data, schedule: { ...schedule, [day]: updated } });
  };

  const removeSlot = (day, idx) => {
    const updated = (schedule[day] || []).filter((_, i) => i !== idx);
    onChange({ ...data, schedule: { ...schedule, [day]: updated } });
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-[#1A2E6E] mb-1.5">Default Session Duration</label>
        <select value={duration} onChange={e => onChange({ ...data, session_duration: +e.target.value })}
          className={inputCls + ' bg-white'}>
          {[30, 45, 60, 90, 120].map(m => <option key={m} value={m}>{m} min</option>)}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-[#1A2E6E] mb-3">Available Days & Time Slots</label>
        <div className="space-y-3">
          {DAYS.map(day => {
            const slots = schedule[day] || [];
            const active = slots.length > 0;
            return (
              <div key={day} className={`rounded-xl border transition-all ${active ? 'border-[#1A2E6E]/30 bg-[#1A2E6E]/3' : 'border-[#E8EAF0]'}`}>
                <button type="button" onClick={() => toggleDay(day)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left">
                  <span className={`font-medium capitalize text-sm ${active ? 'text-[#1A2E6E]' : 'text-[#1A2E6E]/50'}`}>
                    {DAY_LABELS[day]}
                  </span>
                  <div className={`w-10 h-5 rounded-full transition-colors flex items-center px-0.5 ${active ? 'bg-[#1A2E6E]' : 'bg-[#E8EAF0]'}`}>
                    <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform ${active ? 'translate-x-5' : ''}`} />
                  </div>
                </button>
                {active && (
                  <div className="px-4 pb-3 space-y-2">
                    {slots.map((slot, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input type="time" value={slot} onChange={e => updateSlot(day, idx, e.target.value)}
                          className="flex-1 px-3 py-2 rounded-lg border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none text-sm" />
                        <button type="button" onClick={() => removeSlot(day, idx)}
                          className="p-2 text-[#D72B2B]/60 hover:text-[#D72B2B] transition-colors">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                    <button type="button" onClick={() => addSlot(day)}
                      className="flex items-center gap-1.5 text-xs text-[#1A2E6E]/60 hover:text-[#1A2E6E] transition-colors">
                      <Plus size={12} /> Add slot
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ── Step 4: First Student ─────────────────────────────────────────────────────
const Step4 = ({ data, onChange, onCreated }) => {
  const [showPass, setShowPass] = useState(false);
  const [creating, setCreating] = useState(false);

  const create = async () => {
    if (!data.name?.trim() || !data.email?.trim() || !data.password?.trim()) {
      toast.error('Name, email and password are required');
      return;
    }
    if (data.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin/users`, {
        name: data.name.trim(),
        email: data.email.trim(),
        password: data.password,
        phone: data.phone?.trim() || null,
        role: 'student',
        status: data.pre_approve !== false ? 'approved' : 'pending',
      });
      onCreated({ ...data, created: true, student_id: res.data.id });
      toast.success(`Student account created for ${data.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create student');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-5">
      {data.created && (
        <div className="flex items-center gap-3 p-4 bg-[#4CAF50]/10 border border-[#4CAF50]/30 rounded-xl">
          <Check className="w-5 h-5 text-[#4CAF50] shrink-0" />
          <p className="text-sm font-medium text-[#4CAF50]">
            Student account created for <strong>{data.name}</strong>
          </p>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-[#1A2E6E] mb-1.5">Full Name <span className="text-[#D72B2B]">*</span></label>
        <input type="text" value={data.name || ''} onChange={e => onChange({ ...data, name: e.target.value })}
          placeholder="e.g. João Silva" className={inputCls} disabled={data.created} />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#1A2E6E] mb-1.5">Email <span className="text-[#D72B2B]">*</span></label>
        <input type="email" value={data.email || ''} onChange={e => onChange({ ...data, email: e.target.value })}
          placeholder="student@email.com" className={inputCls} disabled={data.created} />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#1A2E6E] mb-1.5">Password <span className="text-[#D72B2B]">*</span></label>
        <div className="relative">
          <input type={showPass ? 'text' : 'password'} value={data.password || ''}
            onChange={e => onChange({ ...data, password: e.target.value })}
            placeholder="At least 8 characters" className={inputCls + ' pr-12'} disabled={data.created} />
          <button type="button" onClick={() => setShowPass(v => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#1A2E6E]/40 hover:text-[#1A2E6E]">
            {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        </div>
      </div>
      <div className="flex items-center gap-3 p-4 bg-[#F8F9FC] rounded-xl border border-[#E8EAF0]">
        <input type="checkbox" id="pre_approve"
          checked={data.pre_approve !== false}
          onChange={e => onChange({ ...data, pre_approve: e.target.checked })}
          disabled={data.created}
          className="w-4 h-4 rounded accent-[#1A2E6E]" />
        <label htmlFor="pre_approve" className="text-sm text-[#1A2E6E]">
          Pre-approve account (grant full dashboard access immediately)
        </label>
      </div>
      {!data.created && (
        <button onClick={create} disabled={creating}
          className="w-full flex items-center justify-center gap-2 py-3 bg-[#1A2E6E] text-white rounded-xl hover:bg-[#16275C] transition-colors font-medium disabled:opacity-50">
          {creating ? <Loader2 size={18} className="animate-spin" /> : <GraduationCap size={18} />}
          Create Student Account
        </button>
      )}
    </div>
  );
};

// ── Step 5: Review & Finish ───────────────────────────────────────────────────
const Step5 = ({ stepData }) => {
  const s1 = stepData[1] || {};
  const s2 = stepData[2] || {};
  const s3 = stepData[3] || {};
  const s4 = stepData[4] || {};

  const items = [
    { label: 'Platform Name', value: s1.site_name, icon: Building2 },
    { label: 'Contact Email', value: s1.contact_email, icon: Building2 },
    { label: 'Teacher Created', value: s2.created ? s2.name : null, icon: UserCheck, skip: s2.skipped },
    { label: 'Schedule', value: s3.skipped ? null : Object.entries(s3.schedule || {}).filter(([, v]) => v?.length).map(([d]) => DAY_LABELS[d]).join(', ') || null, icon: CalendarDays, skip: s3.skipped },
    { label: 'Student Created', value: s4.created ? s4.name : null, icon: GraduationCap, skip: s4.skipped },
  ];

  return (
    <div className="space-y-4">
      <p className="text-[#1A2E6E]/70 text-sm">
        Here's a summary of your setup. You can always update these settings later from the admin panel.
      </p>
      {items.map(({ label, value, icon: Icon, skip }) => (
        <div key={label} className="flex items-center gap-4 p-4 bg-[#F8F9FC] rounded-xl border border-[#E8EAF0]">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${value ? 'bg-[#4CAF50]/10' : 'bg-[#E8EAF0]'}`}>
            <Icon className={`w-5 h-5 ${value ? 'text-[#4CAF50]' : 'text-[#1A2E6E]/30'}`} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-[#1A2E6E]/50 font-medium uppercase tracking-wide">{label}</p>
            <p className={`text-sm font-medium truncate ${value ? 'text-[#1A2E6E]' : 'text-[#1A2E6E]/30'}`}>
              {value || (skip ? 'Skipped' : 'Not configured')}
            </p>
          </div>
          {value && <Check className="w-5 h-5 text-[#4CAF50] shrink-0" />}
        </div>
      ))}
    </div>
  );
};

// ── Main Wizard Component ─────────────────────────────────────────────────────
export const AdminOnboarding = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [stepData, setStepData] = useState({
    1: { site_name: '', site_tagline: '', contact_email: '', timezone: 'America/Sao_Paulo' },
    2: { name: '', email: '', password: '', phone: '', created: false },
    3: { schedule: {}, session_duration: 60 },
    4: { name: '', email: '', password: '', pre_approve: true, created: false },
  });

  // Load saved progress
  useEffect(() => {
    axios
      .get(`${API_URL}/api/admin/onboarding`)
      .then(res => {
        if (res.data.completed) {
          navigate('/admin/dashboard', { replace: true });
          return;
        }
        const saved = res.data.step_data || {};
        setStepData(prev => ({
          1: { ...prev[1], ...(saved[1] || {}) },
          2: { ...prev[2], ...(saved[2] || {}) },
          3: { ...prev[3], ...(saved[3] || {}) },
          4: { ...prev[4], ...(saved[4] || {}) },
        }));
        setCompletedSteps(res.data.completed_steps || []);
        setCurrentStep(res.data.current_step || 1);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [navigate]);

  const saveStep = async (step, data) => {
    // The account (teacher/admin) is already created by the time this fires —
    // password is only ever needed transiently in the form, never persisted.
    const { password, ...safeData } = data;
    try {
      await axios.put(`${API_URL}/api/admin/onboarding/step`, { step, data: safeData });
      setCompletedSteps(prev => prev.includes(step) ? prev : [...prev, step]);
    } catch {
      // Non-blocking — progress may not save but wizard continues
    }
  };

  const handleNext = async () => {
    const data = stepData[currentStep];

    // Step 1: validate + apply to settings
    if (currentStep === 1) {
      if (!data.site_name?.trim() || !data.contact_email?.trim()) {
        toast.error('Platform name and contact email are required');
        return;
      }
      setSaving(true);
      try {
        await axios.put(`${API_URL}/api/admin/settings`, {
          site_name: data.site_name.trim(),
          site_tagline: data.site_tagline?.trim() || undefined,
          contact_email: data.contact_email.trim(),
        });
      } catch {
        toast.error('Failed to save platform settings');
        setSaving(false);
        return;
      }
      setSaving(false);
    }

    await saveStep(currentStep, data);
    setCurrentStep(s => Math.min(s + 1, 5));
  };

  const handleSkip = async () => {
    const skippedData = { ...stepData[currentStep], skipped: true };
    setStepData(prev => ({ ...prev, [currentStep]: skippedData }));
    await saveStep(currentStep, skippedData);
    setCurrentStep(s => Math.min(s + 1, 5));
  };

  const handleBack = () => setCurrentStep(s => Math.max(s - 1, 1));

  const handleFinish = async () => {
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/onboarding/complete`);
      toast.success('Setup complete! Welcome to your dashboard.');
      navigate('/admin/dashboard', { replace: true });
    } catch {
      toast.error('Failed to complete setup');
      setSaving(false);
    }
  };

  const skippable = [2, 3, 4]; // steps that can be skipped

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FC]">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  const stepConfig = STEPS[currentStep - 1];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F9FC] to-[#EEF0F8] flex flex-col">
      {/* Top bar */}
      <div className="w-full bg-white border-b border-[#E8EAF0] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#D72B2B] rounded-lg flex items-center justify-center">
            <Rocket className="w-4 h-4 text-white" />
          </div>
          <span className="font-bold text-[#1A2E6E] font-['Sora'] hidden sm:block">Platform Setup</span>
        </div>
        <span className="text-sm text-[#1A2E6E]/50">Step {currentStep} of {STEPS.length}</span>
      </div>

      <div className="flex-1 flex flex-col items-center justify-start px-4 py-8 sm:py-12">
        <div className="w-full max-w-2xl">

          {/* Stepper */}
          <div className="flex items-center mb-10">
            {STEPS.map((step, idx) => {
              const done = completedSteps.includes(step.id);
              const active = currentStep === step.id;
              const Icon = step.icon;
              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center gap-1 flex-shrink-0">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all ${
                      done    ? 'bg-[#4CAF50] border-[#4CAF50]' :
                      active  ? 'bg-[#1A2E6E] border-[#1A2E6E]' :
                                'bg-white border-[#E8EAF0]'
                    }`}>
                      {done
                        ? <Check className="w-4 h-4 text-white" />
                        : <Icon className={`w-4 h-4 ${active ? 'text-white' : 'text-[#1A2E6E]/30'}`} />
                      }
                    </div>
                    <span className={`text-[10px] font-medium hidden sm:block ${active ? 'text-[#1A2E6E]' : 'text-[#1A2E6E]/40'}`}>
                      {step.title}
                    </span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-2 ${completedSteps.includes(step.id) ? 'bg-[#4CAF50]' : 'bg-[#E8EAF0]'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* Card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-2xl border border-[#E8EAF0] shadow-sm overflow-hidden"
            >
              {/* Card header */}
              <div className="px-6 py-5 border-b border-[#E8EAF0] bg-gradient-to-r from-[#1A2E6E]/5 to-transparent">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#1A2E6E]/10 rounded-xl flex items-center justify-center">
                    <stepConfig.icon className="w-5 h-5 text-[#1A2E6E]" />
                  </div>
                  <div>
                    <h2 className="font-bold text-[#1A2E6E] text-lg font-['Sora']">{stepConfig.title}</h2>
                    <p className="text-sm text-[#1A2E6E]/60">{stepConfig.desc}</p>
                  </div>
                </div>
              </div>

              {/* Card body */}
              <div className="p-6">
                {currentStep === 1 && (
                  <Step1
                    data={stepData[1]}
                    onChange={d => setStepData(prev => ({ ...prev, 1: d }))}
                  />
                )}
                {currentStep === 2 && (
                  <Step2
                    data={stepData[2]}
                    onChange={d => setStepData(prev => ({ ...prev, 2: d }))}
                    onCreated={d => setStepData(prev => ({ ...prev, 2: d }))}
                  />
                )}
                {currentStep === 3 && (
                  <Step3
                    data={stepData[3]}
                    onChange={d => setStepData(prev => ({ ...prev, 3: d }))}
                  />
                )}
                {currentStep === 4 && (
                  <Step4
                    data={stepData[4]}
                    onChange={d => setStepData(prev => ({ ...prev, 4: d }))}
                    onCreated={d => setStepData(prev => ({ ...prev, 4: d }))}
                  />
                )}
                {currentStep === 5 && <Step5 stepData={stepData} />}
              </div>

              {/* Card footer / nav */}
              <div className="px-6 py-4 border-t border-[#E8EAF0] flex items-center justify-between gap-3 bg-[#F8F9FC]">
                <button
                  onClick={handleBack}
                  disabled={currentStep === 1}
                  className="flex items-center gap-2 px-4 py-2.5 border border-[#E8EAF0] text-[#1A2E6E] rounded-xl hover:bg-white transition-colors text-sm font-medium disabled:opacity-30"
                >
                  <ArrowLeft size={16} /> Back
                </button>

                <div className="flex items-center gap-2">
                  {skippable.includes(currentStep) && !stepData[currentStep]?.created && (
                    <button
                      onClick={handleSkip}
                      className="flex items-center gap-1.5 px-4 py-2.5 text-[#1A2E6E]/50 hover:text-[#1A2E6E] rounded-xl transition-colors text-sm"
                    >
                      <SkipForward size={14} /> Skip
                    </button>
                  )}

                  {currentStep < 5 ? (
                    <button
                      onClick={handleNext}
                      disabled={saving}
                      className="flex items-center gap-2 px-5 py-2.5 bg-[#D72B2B] text-white rounded-xl hover:bg-[#B71C1C] transition-colors text-sm font-medium disabled:opacity-50"
                    >
                      {saving ? <Loader2 size={16} className="animate-spin" /> : null}
                      Continue <ArrowRight size={16} />
                    </button>
                  ) : (
                    <button
                      onClick={handleFinish}
                      disabled={saving}
                      className="flex items-center gap-2 px-6 py-2.5 bg-[#4CAF50] text-white rounded-xl hover:bg-[#388E3C] transition-colors font-medium disabled:opacity-50"
                    >
                      {saving ? <Loader2 size={16} className="animate-spin" /> : <Rocket size={16} />}
                      Launch Dashboard
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <p className="text-center text-xs text-[#1A2E6E]/30 mt-6">
            Progress is saved automatically — you can safely close and resume later.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminOnboarding;
